import { z } from "zod";

/**
 * Full-page Google sign-in (OAuth 2.0 authorization code + PKCE). The browser never sees the client secret:
 * the API exchanges the code and returns only the Google ID token, which Firebase Auth accepts as a credential.
 */
const KEY = "cop.oauth";

const b64url = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const random = (n: number) => b64url(crypto.getRandomValues(new Uint8Array(n)));

export const exchangeGoogleCodeInput = z.object({
  code: z.string().min(10).max(2048),
  codeVerifier: z.string().min(43).max(128),
  redirectUri: z.url(),
});

/** Stores the PKCE verifier and state, and returns the Google URL to navigate to. */
export async function googleAuthUrl(opts: { clientId: string; redirectUri: string; returnTo: string }) {
  const verifier = random(64);
  const challenge = b64url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))));
  const state = random(24);
  const returnTo = opts.returnTo.startsWith("/") && !opts.returnTo.startsWith("//") ? opts.returnTo : "/";
  sessionStorage.setItem(KEY, JSON.stringify({ state, verifier, returnTo, redirectUri: opts.redirectUri }));
  const params = new URLSearchParams({
    client_id: opts.clientId,
    redirect_uri: opts.redirectUri,
    response_type: "code",
    scope: "openid email profile",
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    prompt: "select_account",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}

export type GoogleCallback =
  | { kind: "none" }
  | { kind: "cancelled"; returnTo: string }
  | { kind: "invalid" }
  | { kind: "code"; code: string; codeVerifier: string; redirectUri: string; returnTo: string };

/** Reads (and consumes) the OAuth response on the redirect page. */
export function readGoogleCallback(search: string): GoogleCallback {
  const params = new URLSearchParams(search);
  const state = params.get("state");
  if (!state || (!params.has("code") && !params.has("error"))) return { kind: "none" };
  const raw = sessionStorage.getItem(KEY);
  sessionStorage.removeItem(KEY);
  const saved = raw ? (JSON.parse(raw) as { state: string; verifier: string; returnTo: string; redirectUri: string }) : null;
  if (!saved || saved.state !== state) return { kind: "invalid" };
  if (params.has("error")) return { kind: "cancelled", returnTo: saved.returnTo };
  return { kind: "code", code: params.get("code")!, codeVerifier: saved.verifier, redirectUri: saved.redirectUri, returnTo: saved.returnTo };
}
